/**
 * Sanitizes authentication callback URLs to prevent open redirect vulnerabilities.
 *
 * Only internal relative paths are allowed. Absolute URLs, protocol-relative URLs,
 * and javascript/data schemes are rejected.
 *
 * @param input - Raw callback string from query parameter
 * @returns Sanitized internal path or null if invalid
 */
export const sanitizeAuthCallback = (input: string | null): string | null => {
  if (!input) {
    return null
  }

  const trimmed = input.trim()

  if (!trimmed) {
    return null
  }

  // Decode once to normalize encoded slashes
  const decoded = decodeURIComponent(trimmed)

  // Reject if doesn't start with /
  if (!decoded.startsWith("/")) {
    return null
  }

  // Reject protocol-relative URLs (//evil.com)
  if (decoded.startsWith("//")) {
    return null
  }

  // Reject absolute URLs with scheme
  try {
    const url = new URL(decoded, "http://localhost")
    // If the href doesn't match expected format, input had a scheme or was modified
    if (url.href !== `http://localhost${decoded}`) {
      return null
    }
  } catch {
    // URL parsing failed, might be malformed
    return null
  }

  // Reject javascript: and data: schemes (defense in depth)
  const lowerDecoded = decoded.toLowerCase()
  if (
    lowerDecoded.startsWith("javascript:") ||
    lowerDecoded.startsWith("data:")
  ) {
    return null
  }

  return decoded
}

export interface ResolvePostLoginRedirectOptions {
  callback: string | null | undefined
  apiRedirect: string | null | undefined
  fallback: string
}

/**
 * Resolves the post-login redirect URL with precedence:
 * 1. Valid callback query parameter
 * 2. API response redirect
 * 3. Fallback path
 *
 * @param options - Redirect sources with precedence
 * @returns Safe internal redirect path
 */
export const resolvePostLoginRedirect = (
  options: ResolvePostLoginRedirectOptions
): string => {
  const { callback, apiRedirect, fallback } = options

  // Try callback first (highest precedence)
  const sanitizedCallback = sanitizeAuthCallback(callback ?? null)
  if (sanitizedCallback) {
    return sanitizedCallback
  }

  // Try API redirect second
  const sanitizedApiRedirect = sanitizeAuthCallback(apiRedirect ?? null)
  if (sanitizedApiRedirect) {
    return sanitizedApiRedirect
  }

  // Use fallback
  return fallback
}
